import { Injectable } from '@angular/core';
import { Selector } from '@ngxs/store';
import { SortDescriptor } from '@progress/kendo-data-query';
import _ from 'lodash';
import { DirectoryColumn } from 'src/app/samwebDescription/samweb/directory/directory-column';
import { DirectoryOrganisationDataSelectors } from 'src/app/store/samwin/directory/directory-organisation-data/directory-organisation-data.selectors';
import { DirectoryStructureSelectors } from 'src/app/store/samwin/directory/directory-structure/directory-structure.selectors';
import { DirectoryStructureState } from 'src/app/store/samwin/directory/directory-structure/directory-structure.state';
import {
   BooleanOrgField,
   DateTimeOrgField,
   DirectoryEntryRemoteDto,
   DirectoryRestrictionFilterRemoteDto,
   DirectorySearchFilterRemoteDto,
   Int32OrgField,
   OrganisationEntryRemoteDto,
   PagingInfoRemoteDto,
   StringOrgField,
} from 'src/generated/swagger';
import { DirectorySettingsState } from '../../../../brick-settings/specific-brick-settings/directory-settings/directory-settings.state';
import { GridSortState } from '../../../brick-sort-state/grid-sort.state';
import { DirectoryDataBehaviourState } from '../directory-data/directory-data-behaviour.state';
import { DirectoryBrowserBehaviourInstance, DirectoryBrowserBehaviourState } from './directory-browser-behaviour.state';

export interface DirectoryValueEntry {
   id: number;
   value: string | number | boolean | Date;
}

export interface DirectoryData {
   column: DirectoryColumn;
   entries: DirectoryValueEntry[];
}

@Injectable()
export class DirectoryBrowserBehaviourSelectors {
   @Selector([DirectoryDataBehaviourState.getDirectoryEntries])
   public static getSelectedEntryByOrgId(getDirectoryEntries: (brickInstanceId: string) => DirectoryEntryRemoteDto[]) {
      return (brickInstanceId: string, orgId: number) => {
         return getDirectoryEntries(brickInstanceId).find((entry) => entry.id === orgId);
      };
   }

   @Selector([DirectoryDataBehaviourState.getDirectoryEntries, DirectoryBrowserBehaviourState.getSelectedDirectoryID])
   public static getSelectedEntry(
      getDirectoryEntries: (brickInstanceId: string) => DirectoryEntryRemoteDto[],
      getSelectedDirectoryID: (brickInstanceId: string) => number
   ) {
      return (brickInstanceId: string) => {
         const selectedDirectoryID = getSelectedDirectoryID(brickInstanceId);
         return getDirectoryEntries(brickInstanceId).find((entry) => entry.id === selectedDirectoryID);
      };
   }

   @Selector([DirectoryBrowserBehaviourSelectors.getSelectedEntry])
   public static getSelectedNumberFromColumn(getSelectedEntry: (brickInstanceId: string) => DirectoryEntryRemoteDto) {
      return (brickInstanceId: string, columnName: string) => {
         const directoryEntry = getSelectedEntry(brickInstanceId);
         if (_.isNil(directoryEntry)) {
            return null;
         }

         const currentEntry = directoryEntry[columnName];
         // Note: check if entry contains a number directly
         return typeof currentEntry === 'string' ? currentEntry : currentEntry.value;
      };
   }

   @Selector([DirectoryBrowserBehaviourSelectors.getSelectedNumberFromColumn])
   public static getSelectedNumber(getSelectedNumberFromColumn: (brickInstanceId: string, columnName: string) => DirectoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         return getSelectedNumberFromColumn(brickInstanceId, 'orgnum');
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSelectedDirectoryID, DirectoryDataBehaviourState.getDirectoryEntries])
   public static getSelectedDirectoryIndex(
      getSelectedDirectoryID: (brickInstanceId: string) => number,
      getDirectoryEntries: (brickInstanceId: string) => DirectoryEntryRemoteDto[]
   ) {
      return (brickInstanceId: string) => {
         return _(getDirectoryEntries(brickInstanceId)).findIndex((value) => value.id === getSelectedDirectoryID(brickInstanceId));
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getFilterForSubscriberId, DirectorySettingsState.getAutomaticRestrictForIncomingCaller])
   public static getIsCallerFilterPossible(getFilterForSubscriberId: (brickInstanceId: string) => number, filteringEnabled: boolean) {
      return (brickInstanceId: string) => {
         return filteringEnabled && !_.isNil(getFilterForSubscriberId(brickInstanceId));
      };
   }

   @Selector([
      DirectoryBrowserBehaviourState.getIsSearchPhonetic,
      DirectorySettingsState.getDirectorySearchableColumns,
      DirectorySettingsState.getDirectorySearchablePhoneticColumns,
   ])
   public static getBaseBrickSearchColumns(
      isSearchPhonetic: (brickInstanceId: string) => boolean,
      getDirectorySearchableColumns: (brickInstanceId: string) => DirectoryColumn[],
      getDirectorySearchablePhoneticColumns: (brickInstanceId: string) => DirectoryColumn[]
   ) {
      return (brickInstanceId: string) => {
         if (!isSearchPhonetic(brickInstanceId)) {
            return getDirectorySearchableColumns(brickInstanceId);
         } else {
            return getDirectorySearchablePhoneticColumns(brickInstanceId);
         }
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getTemporarySearchColumns, DirectoryBrowserBehaviourSelectors.getBaseBrickSearchColumns])
   public static getColumnsForCurrentSearch(
      getTemporarySearchColumnsForBrick: (brickInstanceId: string) => DirectoryColumn[],
      getBaseBrickSearchColumns: (brickInstanceId: string) => DirectoryColumn[]
   ) {
      return (brickInstanceId: string) => {
         let searchableColumns: DirectoryColumn[] = getBaseBrickSearchColumns(brickInstanceId);
         const temporarySearchColumns = getTemporarySearchColumnsForBrick(brickInstanceId);
         if (!_.isEmpty(temporarySearchColumns)) {
            searchableColumns = _.filter(searchableColumns, (column) =>
               temporarySearchColumns.map((x) => x.directoryColumnKey).includes(column.directoryColumnKey)
            );
         }
         return searchableColumns;
      };
   }

   @Selector([
      DirectorySettingsState.getMinLengthToStartFiltering,
      DirectoryBrowserBehaviourState.getSearchValue,
      DirectorySettingsState.areSettingsFromServerReceived,
      DirectoryBrowserBehaviourState.getFilterForSubscriberId,
   ])
   public static getIsQueryingDirectoryPossible(
      getMinLengthToStartFiltering: (brickInstanceId: string) => number,
      getDirectorySearchValueForBrick: (brickInstanceId: string) => string,
      settingsFromServerReceived: boolean,
      getFilterForSubscriberId: (brickInstanceId: string) => number
   ) {
      return (brickInstanceId: string) => {
         if (!settingsFromServerReceived) {
            return false;
         }
         if (!_.isNil(getFilterForSubscriberId(brickInstanceId))) {
            return true;
         }

         const minLength = getMinLengthToStartFiltering(brickInstanceId);
         const searchValue = getDirectorySearchValueForBrick(brickInstanceId);

         return (_.isEmpty(searchValue) ? 0 : searchValue.length) >= minLength;
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault, DirectorySettingsState.getAutomaticFilterOnSkillOrganisationAssociations])
   public static getIsFilterOnSkillOrganisationAssociationsPossible(
      specificBehaviourState: (brickInstanceId: string) => DirectoryBrowserBehaviourInstance,
      filteringEnabled: boolean
   ) {
      return (brickInstanceId: string) => {
         return filteringEnabled && !_.isEmpty(specificBehaviourState(brickInstanceId).callTriggeredDirectoryAssociations);
      };
   }

   @Selector([DirectorySettingsState.getDefaultSortColumn, GridSortState.getBrickSortConfigurationArray])
   public static getSortDescription(
      getDefaultSortColumn: (brickInstanceId: string) => DirectoryColumn,
      getCustomSorting: (brickInstanceId: string, gridName: string) => SortDescriptor[]
   ) {
      return (brickInstanceId: string) => {
         const customSorting = getCustomSorting(brickInstanceId, 'default');
         if (!_.isNil(customSorting) && !_.isEmpty(customSorting)) {
            return customSorting;
         }
         const defaultSortColumn = getDefaultSortColumn(brickInstanceId);
         if (_.isNil(defaultSortColumn)) {
            return [];
         }
         return [
            <SortDescriptor>{
               field: defaultSortColumn.directoryColumnKey,
               dir: 'asc',
            },
         ];
      };
   }

   @Selector([DirectorySettingsState.getDefaultSelectedDirectory, DirectoryStructureState.directoryStructureId])
   public static getDirectoryRestrictions(getDefaultSelectedDirectory: (brickId: string) => OrganisationEntryRemoteDto, directoryStructureId: string) {
      return (brickId: string) => {
         const restrictionFilter: DirectoryRestrictionFilterRemoteDto[] = [];
         const selectedDirectory = getDefaultSelectedDirectory(brickId);

         if (_.isNil(selectedDirectory)) {
            return null;
         }

         restrictionFilter.push({
            organisationUnit: directoryStructureId,
            entriesToRestrict: [selectedDirectory.id].filter((x) => x >= 0),
            includeNoneLinkedElements: true,
         });

         return restrictionFilter.filter((x) => !_.isEmpty(x.entriesToRestrict));
      };
   }

   @Selector([DirectoryStructureSelectors.getColumnInstance])
   public static getValueFromColumnInstance(
      getColumnInstance: (
         organisationEntryRemoteDto: DirectoryEntryRemoteDto,
         directoryColumn: DirectoryColumn
      ) => Int32OrgField | DateTimeOrgField | BooleanOrgField | StringOrgField
   ) {
      return (entry: DirectoryEntryRemoteDto, column: DirectoryColumn) => {
         const instance = getColumnInstance(entry, column);
         if (instance?.isValueSet) {
            return instance?.value;
         }
         return '';
      };
   }

   @Selector([
      DirectoryStructureState.availableDirectoryColumnsOrdered,
      DirectoryDataBehaviourState.getDirectoryEntries,
      DirectoryBrowserBehaviourSelectors.getValueFromColumnInstance,
   ])
   public static getDirectoryValues(
      availableDirectoryColumns: DirectoryColumn[],
      getDirectoryEntries: (brickInstanceId: string) => DirectoryEntryRemoteDto[],
      getValueFromColumnInstance: (directoryEntry: DirectoryEntryRemoteDto, directoryColumn: DirectoryColumn) => string | number | boolean | Date
   ): (brickId: string) => DirectoryData[] {
      return (brickId: string) => {
         const dirValues = getDirectoryEntries(brickId);
         return availableDirectoryColumns.map(
            (directoryColumn) =>
               <DirectoryData>{
                  column: directoryColumn,
                  entries: dirValues.map((entry) => <DirectoryValueEntry>{ id: entry.id, value: getValueFromColumnInstance(entry, directoryColumn) }),
               }
         );
      };
   }

   @Selector([
      DirectoryBrowserBehaviourState.getSearchValue,
      DirectoryBrowserBehaviourSelectors.getColumnsForCurrentSearch,
      DirectoryOrganisationDataSelectors.directoryOrganisationFilterBySearchValue,
   ])
   public static getSearchValuesForQuery(
      getSearchValue: (brickInstanceId: string) => string,
      getColumnsForCurrentSearch: (brickInstanceId: string) => DirectoryColumn[],
      getDirectoryOrganisationFilterBySearchValue: (searchValue: string, searchInColumns: DirectoryColumn[]) => DirectorySearchFilterRemoteDto[]
   ) {
      return (brickId: string) => {
         return getDirectoryOrganisationFilterBySearchValue(getSearchValue(brickId), getColumnsForCurrentSearch(brickId));
      };
   }

   @Selector([DirectoryDataBehaviourState.getDirectoryEntries])
   public static getPagingForQuery(directoryEntries: (brickInstanceId: string) => DirectoryEntryRemoteDto[]) {
      return (brickId: string) => {
         return <PagingInfoRemoteDto>{
            take: 25,
            skip: directoryEntries(brickId).length,
         };
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getIsSearchPhonetic, GridSortState.getBrickSortConfiguration])
   public static getSortColumnsForQuery(
      getIsSearchPhonetic: (brickInstanceId: string) => boolean,
      getBrickSortConfiguration: (brickInstanceId: string, gridName: string) => SortDescriptor
   ) {
      return (brickId: string) => {
         return !getIsSearchPhonetic(brickId) ? getBrickSortConfiguration(brickId, 'default')?.field ?? 'tbl_org_00_fld_orgnam' : 'tbl_org_00_fld_orgnam';
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getIsSearchPhonetic, GridSortState.getBrickSortConfiguration])
   public static getSortDirectionForQuery(
      getIsSearchPhonetic: (brickInstanceId: string) => boolean,
      getBrickSortConfiguration: (brickInstanceId: string, gridName: string) => SortDescriptor
   ) {
      return (brickId: string) => {
         return !getIsSearchPhonetic(brickId) ? getBrickSortConfiguration(brickId, 'default')?.dir === 'desc' : false;
      };
   }

   @Selector([DirectoryBrowserBehaviourSelectors.getColumnsForCurrentSearch])
   public static getSearchableColumnsForQuery(getColumnsForCurrentSearch: (brickInstanceId: string) => DirectoryColumn[]) {
      return (brickId: string) => {
         return getColumnsForCurrentSearch(brickId)
            .filter((x) => x.organisationUnitNumber === '00')
            .map((x) => x.organisationUnitColumnKey);
      };
   }

   @Selector([
      DirectoryBrowserBehaviourState.getIsCallTriggeredDirectoryAssociationsFilterActive,
      DirectoryBrowserBehaviourSelectors.getIsFilterOnSkillOrganisationAssociationsPossible,
   ])
   private static getUseOrganizationFilter(
      getIsCallTriggeredDirectoryAssociationsFilterActive: (brickId: string) => boolean,
      getIsFilterOnSkillOrganisationAssociationsPossible: (brickId: string) => boolean
   ) {
      return (brickId: string) => {
         return getIsCallTriggeredDirectoryAssociationsFilterActive(brickId) && getIsFilterOnSkillOrganisationAssociationsPossible(brickId);
      };
   }

   @Selector([DirectoryBrowserBehaviourState.getIsCallerFilterActive, DirectoryBrowserBehaviourSelectors.getIsCallerFilterPossible])
   private static getUseSubscriberFilter(getIsCallerFilterActive: (brickId: string) => boolean, getIsCallerFilterPossible: (brickId: string) => boolean) {
      return (brickId: string) => {
         return getIsCallerFilterActive(brickId) && getIsCallerFilterPossible(brickId);
      };
   }

   @Selector([
      DirectoryBrowserBehaviourSelectors.getDirectoryRestrictions,
      DirectoryBrowserBehaviourSelectors.getUseOrganizationFilter,
      DirectoryBrowserBehaviourSelectors.getUseSubscriberFilter,
      DirectoryBrowserBehaviourState.getCallTriggeredDirectoryAssociations,
   ])
   public static getOrganisationRestrictionsForQuery(
      getDirectoryRestrictions: (brickId: string) => DirectoryRestrictionFilterRemoteDto[],
      getUseOrganizationFilter: (brickId: string) => boolean,
      getUseSubscriberFilter: (brickId: string) => boolean,
      getCallTriggeredDirectoryAssociations: (brickId: string) => DirectoryRestrictionFilterRemoteDto[]
   ) {
      return (brickId: string) => {
         let orgFilter = getDirectoryRestrictions(brickId);

         // use only the most precise filter
         // (prefer filtering on subscriber over filtering on skill org-association unit but preserve the org-restriction filter of user)
         if (getUseOrganizationFilter(brickId) && !getUseSubscriberFilter(brickId)) {
            orgFilter = [...getCallTriggeredDirectoryAssociations(brickId), ...orgFilter];
         }

         return orgFilter;
      };
   }

   @Selector([DirectoryBrowserBehaviourSelectors.getUseSubscriberFilter, DirectoryBrowserBehaviourState.getFilterForSubscriberId])
   public static getSubscriberFilterForQuery(getUseSubscriberFilter: (brickId: string) => boolean, getFilterForSubscriberId: (brickId: string) => number) {
      return (brickId: string) => {
         return getUseSubscriberFilter(brickId) ? getFilterForSubscriberId(brickId) : null;
      };
   }
}
